import {
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { castArray, debounce, find, get, toString } from 'lodash';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { NoResultAlert } from 'src/components/noResultAlert';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { MENU_ITEM_WIDTH } from 'src/constants';
import { copyToClipboard, lang } from 'src/utils';

import { DynamicTooltipChip } from '../dynamicTooltipChip/DynamicTooltipChip';
import { InputTextLabel } from '../input/Input.styled';
import {
  HoverIconButton,
  Wrapper,
} from '../widget/selectDropdownWidget/SelectDropdownWidget.styled';
import { OptionType, WidgetsCommonProps } from '../widget/widget.interface';
import { calculateVisibleCount } from './FilterDropdownWithSearch.helper';
import { useDynamicDropdownFetcher } from './FilterDropdownWithSearch.hook';
import {
  ClearIconStyled,
  ClearIconWrapper,
  Container,
  IconWrapper,
  MenuItems,
  MenuItemsText,
  MenuProps,
  SelectedBadgeWrapper,
  SelectedTextWrapper,
  SelectMenu,
} from './FilterDropdownWithSearch.styled';

const MenuElement = ({
  label,
  width,
}: {
  label: string;
  width: number | null | undefined;
}) => {
  return (
    <CustomTooltip
      width={500}
      placement="right"
      title={label}
      overflowWidth={width ?? 300}>
      <MenuItemsText>{label}</MenuItemsText>
    </CustomTooltip>
  );
};

export const FilterDropdownWithSearch = forwardRef(
  (props: WidgetsCommonProps, ref) => {
    const {
      value = [],
      onChange,
      label,
      disabled,
      multiline,
      hasError,
      error,
      badgeOptions,
      canCopy,
      placeholder,
      onRemove,
      onNotExistedEntry,
      inputValidator,
      menuItemWidth,
      name,
      field: displayCount,
      inputFieldSx,
      selectPadding,
      selectOptions: localSelectOptions,
      options,
    } = props;

    const [searchText, setSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const [inputBoxStatus, setInputBoxStatus] = useState('');
    const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);

    const [fetchPreSelectedOptions, setFetchPreSelectedOptions] = useState(
      value.length > 0,
    );

    const {
      options: fetchedOptions,
      isFetching,
      getNextPage,
    } = useDynamicDropdownFetcher(
      name ?? '',
      // fetch any preSelected tags first, ex. url Querys and saved Component tags
      fetchPreSelectedOptions ? value : searchText,
    ); //, values

    const selectedOptionsMap = useMemo(() => {
      const map: Record<string, OptionType> = {};

      localSelectOptions?.forEach((opt) => {
        map[opt.value.toString()] = opt;
      });

      selectedOptions.forEach((opt) => {
        map[opt.value.toString()] = opt;
      });

      return map;
    }, [selectedOptions, localSelectOptions]);

    const unresolvedSelectedValues = useMemo(
      () =>
        castArray(value).filter(
          (selectedValue) =>
            !selectedOptionsMap[toString(selectedValue)]?.label,
        ),
      [selectedOptionsMap, value],
    );

    const shouldHoldSelectedValueRender =
      castArray(value).length > 0 &&
      unresolvedSelectedValues.length > 0 &&
      (fetchPreSelectedOptions || isFetching);

    useEffect(() => {
      if (!Array.isArray(fetchedOptions)) return;

      setSelectedOptions((prev) => {
        const map = new Map(prev.map((opt) => [opt.value, opt]));
        fetchedOptions.forEach((opt) => {
          map.set(opt.value, opt);
        });

        return Array.from(map.values());
      });
      !isFetching && setFetchPreSelectedOptions(false);
    }, [fetchedOptions, isFetching]);

    useEffect(() => {
      if (Array.isArray(value) && value.length > 0) {
        const hasUnresolvedSelections = value.some(
          (selectedValue) =>
            !selectedOptionsMap[toString(selectedValue)]?.label,
        );

        if (hasUnresolvedSelections) {
          setFetchPreSelectedOptions(true);
        }
      }
      // We only want to re-trigger preselected fetches when the selected ids change.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const debouncedSetSearchText = useMemo(
      () => debounce((text: string) => setSearchText(text), 400),
      [],
    );

    const handleChange = useCallback(
      (event: SelectChangeEvent) => {
        const rawValues = castArray(event.target.value);
        const sanitizedValues = rawValues.flatMap((val) =>
          encodeURIComponent(val)
            .toString()
            .split('&')
            .map((v) => decodeURIComponent(v.trim()))
            .filter(Boolean),
        );

        const normalizedSanitized = sanitizedValues.map((v) => {
          const numVal = Number(v);

          return isNaN(numVal) || String(numVal) !== v ? v : numVal;
        });

        onChange(normalizedSanitized);
      },
      [onChange],
    );

    const handleDeleteChip = useCallback(
      (badge: string | number) => {
        // Normalize badge to match the type in value array
        const normalizedBadge =
          typeof value[0] === 'number' ? Number(badge) : badge;
        const newValue = value.filter(
          (v: string | number) =>
            toString(v) !== toString(normalizedBadge) &&
            Number(v) !== Number(normalizedBadge),
        );
        onChange(newValue);
      },
      [value, onChange],
    );

    const handleNotExistedEntry = useCallback(
      (input: string) => {
        const matched = find(
          fetchedOptions,
          (o) => o.label.toLowerCase() === input.toLowerCase(),
        );
        if (matched) {
          onChange([...(value || []), matched.value]);
        } else {
          onNotExistedEntry?.(input);
        }
      },
      [fetchedOptions, onChange, onNotExistedEntry, value],
    );

    const onClose = () => {
      setInputBoxStatus('');
      setOpen(false);
      setSearchText('');
    };

    const isOwnerOperatorTagFilter =
      label === lang.locationOwnerTagsName ||
      label === lang.ownerOperator.ownerOperatorTags;

    const validateInput = useCallback(
      (val: string): string => {
        const result = inputValidator?.(val) ?? '';
        setInputBoxStatus(result);

        return result;
      },
      [inputValidator],
    );

    const renderValue = useCallback(
      (selected: string | string[]) => {
        const selectedArray = castArray(selected);

        if (shouldHoldSelectedValueRender) {
          return <SelectedTextWrapper sx={inputFieldSx} />;
        }

        if (badgeOptions?.align === 'center') {
          if (selectedArray.length === 0) {
            return (
              <SelectedTextWrapper sx={inputFieldSx}>
                {placeholder ?? ''}
              </SelectedTextWrapper>
            );
          }

          const resolvedSelectedValues = selectedArray.flatMap((val) => {
            const resolvedLabel = selectedOptionsMap[val.toString()]?.label;

            if (isOwnerOperatorTagFilter && !resolvedLabel) {
              return [];
            }

            return [
              {
                value: val,
                label: resolvedLabel ?? val.toString(),
              },
            ];
          });

          if (resolvedSelectedValues.length === 0 && selectedArray.length > 0) {
            return <SelectedTextWrapper sx={inputFieldSx} />;
          }

          const visibleCount = calculateVisibleCount(
            resolvedSelectedValues.map((v) => v.label),
          );
          const overflowCount = resolvedSelectedValues.length - visibleCount;
          const visibleValues = resolvedSelectedValues.slice(0, visibleCount);
          const overflowLabels = resolvedSelectedValues
            .slice(visibleCount)
            .map(({ label }) => label)
            .join(', ');

          return (
            <SelectedBadgeWrapper>
              {visibleValues.map(({ value: val, label: badgeLabel }) => {
                return (
                  <DynamicTooltipChip
                    key={val.toString()}
                    name={name}
                    tooltipTitle={badgeLabel}
                    tooltipWidth={500}
                    tooltipActivationWidth={MENU_ITEM_WIDTH}
                    tooltipPlacement="top"
                    replaceSpaces={false}
                    removeBackslashes={true}
                    color={get(badgeOptions, 'color', 'primary')}
                    forceTooltip={visibleCount > 2}
                    sx={{
                      ...badgeOptions?.sx,
                      ...(visibleCount > 2 && {
                        maxWidth: '110px',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }),
                    }}
                    chipName={badgeLabel}
                    size="small"
                    onDelete={() => handleDeleteChip(val)}
                    onMouseDown={(event) => event.stopPropagation()}
                    deleteIcon={badgeOptions?.deleteIcon}
                  />
                );
              })}

              {overflowCount > 0 && (
                <CustomTooltip
                  title={overflowLabels}
                  width={500}
                  placement="top"
                  skipValidateOverflow={true}>
                  <div>
                    <DynamicTooltipChip
                      chipName={`+${overflowCount}`}
                      color={get(badgeOptions, 'color', 'primary')}
                      size="small"
                      onMouseDown={(event) => event.stopPropagation()}
                      sx={badgeOptions?.sx}
                      deleteIcon={badgeOptions?.deleteIcon}
                    />
                  </div>
                </CustomTooltip>
              )}
            </SelectedBadgeWrapper>
          );
        }

        return (
          <SelectedTextWrapper>
            {selectedArray.length > 0
              ? selectedArray
                  .map(
                    (val) => selectedOptionsMap[val.toString()]?.label ?? val,
                  )
                  .join(' , ')
              : placeholder ?? ''}
          </SelectedTextWrapper>
        );
      },
      [
        badgeOptions,
        handleDeleteChip,
        inputFieldSx,
        isOwnerOperatorTagFilter,
        name,
        placeholder,
        selectedOptionsMap,
        shouldHoldSelectedValueRender,
      ],
    );

    // trigger fetch early for smoother scrolling
    const [preLoadInViewRef, preLoadInView] = useInView({
      threshold: 0,
      triggerOnce: true,
    });
    const preLoadTrigger = Math.max(fetchedOptions.length - 11, 0);
    // last item catch for fast scrollers
    const [lastLoadInViewRef, lastLoadInView] = useInView({
      threshold: 0,
      triggerOnce: true,
    });
    const lastLoadTrigger = fetchedOptions.length - 1;

    useEffect(() => {
      if (!isFetching && (preLoadInView || lastLoadInView)) {
        getNextPage();
      }
    }, [isFetching, preLoadInView, lastLoadInView, getNextPage]);

    return (
      <Container>
        <Wrapper>
          {badgeOptions?.align === 'top' && (
            <Stack
              direction="row"
              gap="5px"
              flexWrap="wrap"
              sx={{ overflow: 'auto', maxHeight: '104px' }}>
              {!shouldHoldSelectedValueRender &&
                castArray(value).map((badge, id) => {
                  const badgeLabel =
                    selectedOptionsMap[badge.toString()]?.label ||
                    badge.toString();

                  return (
                    <DynamicTooltipChip
                      key={id}
                      name={name}
                      tooltipTitle={badgeLabel}
                      tooltipWidth={500}
                      tooltipActivationWidth={MENU_ITEM_WIDTH}
                      tooltipPlacement="right"
                      replaceSpaces={false}
                      removeBackslashes={true}
                      color={get(badgeOptions, 'color', 'primary')}
                      chipName={badgeLabel}
                      onDelete={() => handleDeleteChip(badge)}
                    />
                  );
                })}
            </Stack>
          )}

          <FormControl fullWidth>
            <InputTextLabel>{label}</InputTextLabel>
            <SelectMenu
              fullWidth
              ref={ref}
              sx={inputFieldSx}
              disabled={disabled}
              $rounded={badgeOptions?.roundedSelect}
              $selectPadding={selectPadding}
              multiple={multiline}
              open={open}
              MenuProps={{ ...MenuProps, autoFocus: false }}
              value={value.map((v: string | number) => toString(v))}
              error={hasError}
              onChange={handleChange}
              onClose={onClose}
              onOpen={() => {
                setOpen(true);
                setFetchPreSelectedOptions(false);
              }}
              displayEmpty
              renderValue={renderValue}
              inputProps={{ id: 'filter-select' }}>
              <ListSubheader disableGutters={true}>
                <TextField
                  autoFocus
                  autoComplete="off"
                  placeholder={placeholder || 'Filter'}
                  fullWidth
                  error={!!inputBoxStatus}
                  helperText={inputBoxStatus}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    validateInput(val);
                    debouncedSetSearchText(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val && validateInput(val) === '') {
                        handleNotExistedEntry(val);
                        setOpen(false);
                      }
                      onClose();
                    } else if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
              {fetchedOptions.map((option: OptionType, index: number) => {
                return (
                  <MenuItems
                    // data-testid={`options-${option.label}`}
                    key={option.value}
                    value={toString(option.value)}
                    disabled={option.disabled}
                    ref={
                      index === preLoadTrigger
                        ? preLoadInViewRef
                        : index === lastLoadTrigger
                        ? lastLoadInViewRef
                        : null
                    }
                    hoverColor={options?.hoverColor}>
                    <MenuElement width={menuItemWidth} label={option.label} />
                    {canCopy && (
                      <HoverIconButton
                        // data-testid={`option-copy-button-${option.label}`}
                        onClick={(e: { stopPropagation: () => void }) => {
                          e.stopPropagation();
                          copyToClipboard(option.label);
                        }}>
                        <ContentCopyIcon />
                      </HoverIconButton>
                    )}
                  </MenuItems>
                );
              })}

              {fetchedOptions.length === 0 &&
                searchText !== '' &&
                !isFetching && <NoResultAlert dataToCheck={fetchedOptions} />}
            </SelectMenu>
            {displayCount && <Typography>{displayCount}</Typography>}
          </FormControl>

          {badgeOptions?.align === 'bottom' && (
            <Stack direction="row" gap="5px" flexWrap="wrap">
              {!shouldHoldSelectedValueRender &&
                castArray(value).map((badge) => {
                  const badgeLabel =
                    selectedOptionsMap[badge.toString()]?.label ||
                    badge.toString();

                  return (
                    <DynamicTooltipChip
                      key={badge}
                      chipName={badgeLabel}
                      color={get(badgeOptions, 'color', 'primary')}
                      onDelete={() => handleDeleteChip(badge)}
                    />
                  );
                })}
            </Stack>
          )}

          {hasError && <FormHelperText>{error}</FormHelperText>}
        </Wrapper>

        {onRemove && (
          <ClearIconWrapper>
            <IconWrapper onClick={onRemove}>
              <ClearIconStyled />
            </IconWrapper>
          </ClearIconWrapper>
        )}
      </Container>
    );
  },
);
